import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { useAuth } from '../auth/AuthContext.jsx';
import { useWorkspace } from '../workspace/WorkspaceContext.jsx';
import { createApiClient } from '../http/createApiClient.js';
import WorkspaceView from './WorkspaceView.jsx';
import AnswerView from './AnswerView.jsx';
import { Icon, Mark } from './icons.jsx';
import { ACTIVE, startersFor, sameContext } from './presentation.js';
import { visibleModules } from '../modules/registry.js';
import { boundaryFor, getPageContext, requestContext, subscribePageContext } from './context.js';
import './orbit-ai.css';
import { OrbitAIAvailability } from './availability.js';

const api = createApiClient({ auth: true });
function Panel({ boundary, page, open, close, onModalChange }) {
  const moduleParams = { boundary: boundary.kind, ...(boundary.moduleId ? { module_id: boundary.moduleId } : {}), ...(boundary.clientId ? { client_id: boundary.clientId } : {}) };
  const capabilitiesUrl = '/orbit-ai/capabilities?' + new URLSearchParams(moduleParams);
  const [capabilities, setCapabilities] = useState(null);
  const [conversation, setConversation] = useState(null);
  const [history, setHistory] = useState([]);
  const [turns, setTurns] = useState([]);
  const [scope, setScope] = useState('page');
  const [question, setQuestion] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState(null);
  const [reload, setReload] = useState(0);
  const [ready, setReady] = useState(false);
  const alive = useRef(true);
  const [expanded, setExpanded] = useState(false);
  const [links, setLinks] = useState([]);
  const drafts = useRef(new Map());
  const request = useRef(null);
  const historyEpoch = useRef(0);
  const aborts = useRef(new Set());
  const selection = useRef('initial');
  const selectionEpoch = useRef(0);
  const [detachedClarification, setDetachedClarification] = useState(null);
  const listEpoch = useRef(0), filters = useRef({ search: '', archived: false });
  const [listCursor, setListCursor] = useState(null), [turnCursor, setTurnCursor] = useState(null);
  const [historyBusy, setHistoryBusy] = useState(false), [reconnecting, setReconnecting] = useState('');
  const [followup, setFollowup] = useState(null);
  const scopes = useRef(new Map());
  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; historyEpoch.current++; aborts.current.forEach(a => a.abort()); };
  }, []);
  function clearContent() {
    selectionEpoch.current++; historyEpoch.current++; listEpoch.current++; setTurns([]); setHistory([]); setConversation(null);
    selection.current='initial'; setFollowup(null); setListCursor(null); setTurnCursor(null); setExpanded(false); scopes.current.clear(); setReconnecting('');
    setPending(null); setQuestion(''); setReady(false); drafts.current.clear(); request.current = null;
  }
  async function call(method, url, body) {
    const abort = new AbortController(); aborts.current.add(abort);
    try { return await api.request(url, { method, body, signal: abort.signal }); }
    catch (e) {
      if (alive.current && [401, 403].includes(e.status)) { clearContent(); setError(e.message); }
      throw e;
    }
    finally { aborts.current.delete(abort); }
  }
  async function refreshUsage() {
    try {
      const caps=await call('GET',capabilitiesUrl);
      if (!alive.current) return;
      setCapabilities(caps);
      if (!caps.enabled) clearContent();
    } catch { /* Preserve the active turn on transient budget refresh errors. */ }
  }
  async function load(id, cursor = null) {
    const epoch = ++historyEpoch.current;
    setReady(false);
    const result = await call('GET', '/orbit-ai/conversations/' + id + (cursor ? '?cursor=' + encodeURIComponent(cursor) : ''));
    if (!alive.current || epoch !== historyEpoch.current) return;
    selection.current='saved'; setConversation(result.conversation);
    setTurns(old => cursor ? [...result.turns, ...old.filter(t => !result.turns.some(n => n.id === t.id))] : result.turns);
    setTurnCursor(result.next_cursor || null); setReady(true);
    if (!cursor) setScope(scopes.current.get(id) || result.turns.at(-1)?.scope || 'page');
    if (!cursor) setPending(result.turns.find(t => ACTIVE.has(t.status))?.id || null);
  }
  async function listConversations(cursor = null, search = filters.current.search, archived = filters.current.archived) {
    const epoch=++listEpoch.current;
    filters.current={ search, archived }; setHistoryBusy(true);
    try {
      const query = new URLSearchParams({ ...moduleParams, search, archived: String(archived) });
      if (cursor) query.set('cursor', cursor);
      const list=await call('GET', '/orbit-ai/conversations?' + query);
      if (!alive.current || epoch!==listEpoch.current) return null;
      setHistory(items => cursor ? [...items, ...list.items.filter(c => !items.some(i => i.id===c.id))] : list.items);
      setListCursor(list.next_cursor || null); return list;
    } catch(e) { if (alive.current && e.name!=='AbortError') setError(e.message); return null; }
    finally { if (alive.current && epoch===listEpoch.current) setHistoryBusy(false); }
  }
  useEffect(() => {
    if (!open) return;
    let active = true;
    const epoch = selectionEpoch.current;
    setError(''); setCapabilities(null); setReady(false); setLinks([]);
    (async () => {
      try {
        const [caps, permissions] = await Promise.all([
          call('GET', capabilitiesUrl),
          call('GET', '/portal/modules').catch(() => null),
        ]);
        if (!active || !alive.current) return;
        setCapabilities(caps);
        if (permissions) setLinks(visibleModules(permissions.grants, permissions.isGlobalAdmin, permissions.actingAs).map(m => m.id));
        if (!caps.enabled) { clearContent(); return; }
        const list = await listConversations();
        if (!active || !alive.current) return;
        if (!list || epoch !== selectionEpoch.current) return;
        if (conversation?.id) await load(conversation.id);
        else if (selection.current==='initial' && list.items[0]?.id) await load(list.items[0].id);
        else setReady(true);
      } catch (e) { if (active && e.name !== 'AbortError') { setTurns([]); setError(e.message); } }
    })();
    return () => { active = false; };
  }, [open, boundary.kind, reload]);
  useEffect(() => {
    if (!pending || !open) return;
    let active = true, timer, failures = 0;
    const poll = async () => {
      try {
        const turn = await call('GET', '/orbit-ai/turns/' + pending);
        if (!active || !alive.current) return;
        failures=0; setReconnecting('');
        setTurns(old => old.map(t => t.id === turn.id ? { ...t, ...turn } : t));
        if (ACTIVE.has(turn.status)) timer = setTimeout(poll, 2500);
        else { setPending(null); if (conversation?.id) await load(conversation.id); await refreshUsage(); }
      } catch (e) {
        if (!active || e.name === 'AbortError') return;
        if ([401,403].includes(e.status)) return;
        if (e.status===404 || e.status===410) {
          setPending(null); setError('This analysis is no longer available. Reload the conversation.');
          if (conversation?.id) load(conversation.id).catch(() => {});
          return;
        }
        failures++;
        setReconnecting(failures<8 ? 'Connection interrupted. Reconnecting to your analysis...' : 'Still unable to reconnect. Reload to resume checking.');
        if (failures<8) timer=setTimeout(poll, Math.min(30000, 1000 * 2 ** failures));
      }
    };
    timer = setTimeout(poll, 2000);
    return () => { active = false; clearTimeout(timer); };
  }, [pending, open, conversation?.id, reload]);
  useEffect(() => {
    const handler = event => { if (event.detail?.question) { setQuestion(event.detail.question); } };
    window.addEventListener('orbit-ai-open', handler);
    return () => window.removeEventListener('orbit-ai-open', handler);
  }, []);
  function newChat() {
    if (busy || pending) return;
    if (conversation?.id) {
      drafts.current.set(conversation.id, { question, followup }); scopes.current.set(conversation.id,scope);
      setHistory(items => [conversation, ...items.filter(c => c.id !== conversation.id)]);
    }
    selectionEpoch.current++; selection.current='new'; setFollowup(null); setTurnCursor(null); setScope('page');
    historyEpoch.current++; setConversation(null); setTurns([]); setPending(null); setQuestion('');
    setReady(true); setError(''); request.current = null;
  }
  const latest = turns.at(-1);
  const continuationTarget = followup || (latest?.status === 'needs_clarification' &&
    latest.id !== detachedClarification && latest.scope === scope && sameContext(latest.context, page) ? latest : null);
  function detachFollowup() { setFollowup(null); setDetachedClarification(latest?.id || null); }
  async function send(event, override) {
    event?.preventDefault();
    const text = override?.question || question;
    if (!text.trim() || !page || busy || pending || !ready || !capabilities?.enabled || capabilities.analysis_ready === false) return;
    const target=override?.fresh ? null : override?.replyTo || continuationTarget;
    const frozen = structuredClone(override?.context || target?.context || page);
    delete frozen.datasets;
    const selectedScope = override?.scope || target?.scope || scope;
    setBusy(true); setError('');
    try {
      let convo = conversation;
      if (!convo) {
        convo = await call('POST', '/orbit-ai/conversations', moduleParams);
        if (!alive.current) return;
        setConversation(convo);
      }
      const body = { question: text, scope: selectedScope, context: frozen, revision: convo.revision };
      if (target) { body.reply_to=target.id; body.continuation=override?.continuation || 'followup'; }
      const serialized = JSON.stringify(body);
      if (request.current?.serialized !== serialized) request.current = { serialized, key: crypto.randomUUID() };
      const result = await call('POST', '/orbit-ai/conversations/' + convo.id + '/turns',
        { ...body, idempotency_key: request.current.key });
      if (!alive.current) return;
      setScope(selectedScope); scopes.current.set(convo.id, selectedScope); setQuestion(''); setFollowup(null); request.current = null;
      setPending(result.id);
      await load(convo.id); await refreshUsage();
    } catch (e) {
      if (alive.current && e.name !== 'AbortError') {
        setError(e.message);
        if (e.status === 409 && conversation) await load(conversation.id).catch(() => {});
      }
    } finally { if (alive.current) setBusy(false); }
  }
  async function cancel() {
    try { await call('POST', '/orbit-ai/turns/' + pending + '/cancel', {}); if (!alive.current) return; setPending(null); await load(conversation.id); }
    catch (e) { if (alive.current) setError(e.message); }
  }
  function chooseConversation(item) {
    if (busy || pending) return;
    selectionEpoch.current++;
    if (conversation?.id) { drafts.current.set(conversation.id, { question, followup }); scopes.current.set(conversation.id,scope); }
    const draft=drafts.current.get(item.id);
    setQuestion(draft?.question || ''); setFollowup(draft?.followup || null); setError(''); setBusy(true);
    load(item.id).catch(e => { if (alive.current && e.name !== 'AbortError') setError(e.message); })
      .finally(() => { if (alive.current) setBusy(false); });
  }
  async function remove(id) {
    if (busy || pending) return;
    setBusy(true);
    try {
      await call('DELETE', '/orbit-ai/conversations/' + id);
      if (!alive.current) return;
      drafts.current.delete(id);
      setHistory(items => items.filter(c => c.id !== id));
      if (conversation?.id === id) { selectionEpoch.current++; selection.current='new'; setFollowup(null); setTurnCursor(null); historyEpoch.current++; setConversation(null); setTurns([]); setQuestion(''); setPending(null); setReady(true); request.current = null; }
    } catch (e) { if (alive.current && e.name !== 'AbortError') setError(e.message); }
    finally { if (alive.current) setBusy(false); }
  }
  async function editConversation(item, changes) {
    if (busy || pending) return;
    setBusy(true); setError('');
    try {
      const updated=await call('PUT','/orbit-ai/conversations/'+item.id,{ ...changes, revision:item.revision });
      if (!alive.current) return;
      if (conversation?.id===item.id) setConversation(old => ({...old,...updated}));
      await listConversations();
    } catch(e) { if (alive.current && e.name!=='AbortError') setError(e.message); }
    finally { if (alive.current) setBusy(false); }
  }
  function prepareFollowup(text, turn) { setQuestion(text); setFollowup(turn); setScope(turn.scope); }
  function navigate(path) { window.history.pushState(null, '', path); window.dispatchEvent(new PopStateEvent('popstate')); }
  const canCompose = !!(capabilities?.enabled && capabilities.analysis_ready !== false && page && ready && !conversation?.archived);
  let statusContent = null;
  if (!capabilities && !error) statusContent = <div className="aiw-state"><Mark large /><h3>Getting your workspace ready</h3><p>Checking available data and conversations.</p><div className="aiw-loading-line o-skel" /></div>;
  else if (error && !ready) statusContent = <div className="aiw-state"><Icon name="shield" size={28} /><h3>Conversation unavailable</h3><p>Access or connectivity needs attention. Reload to check again.</p></div>;
  else if (capabilities && !capabilities.enabled) statusContent = <div className="aiw-state"><div className="aiw-orbit-art" aria-hidden="true"><span /><span /><Mark large /></div>
    <h3>{boundary.kind === 'demo' ? 'Orbit AI is not configured yet' : 'Start from a supported dashboard'}</h3>
    <p>{boundary.kind === 'demo' ? capabilities.reason : 'Orbit AI currently analyzes synthetic Dashboard data. Analysis for this workspace is not available yet.'}</p>
    {boundary.kind !== 'demo' && links.includes('dashboard') && <button className="o-btn o-btn-primary" onClick={() => navigate('/dashboard/paid-media')}>Open Campaign performance<Icon name="arrow" size={16} /></button>}
    {boundary.kind === 'demo' && links.includes('settings') && <button className="o-btn" onClick={() => { navigate('/settings/ai/orbit-ai'); close(); }}>Open AI settings</button>}
  </div>;
  else if (capabilities?.enabled && !page) statusContent = <div className="aiw-state"><Icon name="source" size={28} /><h3>Select a page to explore</h3><p>Open Overview, Campaign, or CRM and apply a date range to begin.</p>{links.includes('dashboard') && <button className="o-btn" onClick={() => navigate('/dashboard/paid-media')}>Open Campaign performance</button>}</div>;
  else if (!ready && !turns.length) statusContent = <div className="aiw-state"><p>Loading your conversation...</p></div>;
  const conversations = filters.current.search || filters.current.archived ? history : [conversation, ...history.filter(c => c.id !== conversation?.id)].filter(c => c && !c.archived);
  return <WorkspaceView open={open} expanded={expanded} onExpanded={setExpanded} onClose={close} onModalChange={onModalChange}
    boundary={boundary} page={page} capabilities={capabilities} canCompose={canCompose} busy={busy} pending={pending}
    turns={turns} draft={question} onDraft={setQuestion} onSend={text => send(null, { question: text })}
    conversation={conversation} reconnecting={reconnecting} followup={continuationTarget} onClearFollowup={detachFollowup}
    historyReady={ready} turnCursor={turnCursor} onEarlier={() => load(conversation.id,turnCursor).catch(e=>setError(e.message))}
    listCursor={listCursor} historyBusy={historyBusy} onSearchHistory={(q,a)=>listConversations(null,q,a)}
    onMoreHistory={() => listConversations(listCursor)} onEditConversation={editConversation}
    onCancel={cancel} onNew={newChat} scope={scope} onScope={value => { setScope(value); if (conversation) scopes.current.set(conversation.id,value); detachFollowup(); }} conversations={conversations}
    onConversation={chooseConversation} onDelete={remove} starters={startersFor(page, capabilities?.datasets)}
    statusContent={statusContent} error={error} onReload={() => setReload(n => n + 1)}
    renderAnswer={(turn, viewProps) => <AnswerView key={turn.id} turn={turn} {...viewProps} onFollowup={text => { prepareFollowup(text, turn); viewProps.onFollowup?.(text); }} disabled={!canCompose || busy || !!pending}
      onScope={t => send(null, { question: t.question, scope: 'module', context: t.context, replyTo:t, continuation:'scope' })}
      onRetry={t => send(null, { question: t.question, scope: t.scope, context: t.context, fresh:true })}
      onExplain={t => send(null,{question:t.question,scope:t.scope,context:t.context,replyTo:t,continuation:'explain'})}
      onRate={async (id, rating) => { await call('POST', '/orbit-ai/turns/' + id + '/feedback', { rating }); if (alive.current) setTurns(items=>items.map(t=>t.id===id?{...t,feedback:rating}:t)); }} />} />;
}

export default function OrbitAIProvider({ children }) {
  const { user } = useAuth();
  const workspace = useWorkspace();
  const [path, setPath] = useState(window.location.pathname);
  const [open, setOpen] = useState(window.location.pathname === '/orbit-ai');
  const launcher = useRef(null);
  const [modal, setModal] = useState(false);
  const snapshot = useSyncExternalStore(subscribePageContext, getPageContext, () => null);
  useEffect(() => {
    const nav = () => { setPath(window.location.pathname); if (window.location.pathname === '/orbit-ai') setOpen(true); };
    const show = () => setOpen(true);
    window.addEventListener('popstate', nav); window.addEventListener('orbit-ai-open', show);
    return () => { window.removeEventListener('popstate', nav); window.removeEventListener('orbit-ai-open', show); };
  }, []);
  useEffect(() => {
    const previous = document.body.style.overflow;
    if (modal && open) document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previous; };
  }, [modal, open]);
  useEffect(() => {
    const viewport = window.visualViewport;
    const resize = () => document.documentElement.style.setProperty('--orbit-ai-viewport', (viewport?.height || innerHeight) + 'px');
    resize(); viewport?.addEventListener('resize', resize);
    return () => { viewport?.removeEventListener('resize', resize); document.documentElement.style.removeProperty('--orbit-ai-viewport'); };
  }, []);
  const boundary = boundaryFor(path, workspace);
  const identity = String(user?.id || user?.username || '') + ':' + String(user?.actingAs?.id || '');
  const page = requestContext(snapshot, path);
  const [availability, setAvailability] = useState(null);
  const available = availability?.identity === identity && availability.enabled && !page?.ai_disabled;
  useEffect(() => {
    if (!user || path === '/login') { setAvailability(null); return; }
    let alive = true, request = 0;
    const controller = new AbortController();
    const refresh = async () => {
      const current = ++request;
      try {
        const result = await api.get('/orbit-ai/availability', { signal: controller.signal });
        if (alive && current === request) {
          setAvailability({ identity, enabled: result.enabled === true });
        }
      } catch (e) {
        if (alive && current === request && e.name !== 'AbortError') setAvailability({ identity, enabled: false });
      }
    };
    refresh();
    const timer = setInterval(refresh, 30000);
    window.addEventListener('focus', refresh);
    window.addEventListener('orbit-ai-availability-changed', refresh);
    return () => { alive = false; controller.abort(); clearInterval(timer); window.removeEventListener('focus', refresh); window.removeEventListener('orbit-ai-availability-changed', refresh); };
  }, [identity, !!user, path === '/login']);
  useEffect(() => {
    if (!available) { setOpen(false); setModal(false); }
    else if (window.location.pathname === '/orbit-ai') setOpen(true);
  }, [available]);
  function close() { setOpen(false); requestAnimationFrame(() => launcher.current?.focus()); }
  useEffect(() => {
    const escape = event => { if (open && event.key === 'Escape' && !event.defaultPrevented) close(); };
    window.addEventListener('keydown', escape);
    return () => window.removeEventListener('keydown', escape);
  }, [open]);
  return <OrbitAIAvailability.Provider value={!!user && !!available}><div className={user && available && open ? 'orbit-ai-shell' : undefined}>
    <div className="orbit-ai-content" inert={user && available && open && modal ? '' : undefined}>{children}</div>
    {user && available && path !== '/login' && <>
      {!open && <button ref={launcher} className="o-btn o-btn-primary orbit-ai-launcher" onClick={() => setOpen(true)} aria-expanded={false}>Orbit AI</button>}
      {open && modal && <div className="orbit-ai-backdrop" aria-hidden="true" />}
      <Panel key={identity + ':' + boundary.key} boundary={boundary} page={page} open={open} close={close} onModalChange={setModal} />
    </>}
  </div></OrbitAIAvailability.Provider>;
}
